import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Onboarding from "./pages/Onboarding";
import DashboardRouter from "./pages/DashboardRouter";
import Profile from "./pages/Profile";
import MediaKit from "./pages/MediaKit";
import CRM from "./pages/CRM";
import Outreach from "./pages/Outreach";
import Pricing from "./pages/Pricing";
import Settings from "./pages/Settings";
import Positioning from "./pages/Positioning";
import DealAssistant from "./pages/DealAssistant";
import PublicKit from "./pages/PublicKit";
import Terms from "./pages/legal/Terms";
import Privacy from "./pages/legal/Privacy";
import CommercialTerms from "./pages/legal/CommercialTerms";
import SponsorFinder from "./pages/SponsorFinder";
import BrandDirectory from "./pages/BrandDirectory";
import Admin from "./pages/admin/Admin";
import ForBrands from "./pages/ForBrands";
import BrandOnboarding from "./pages/brand/BrandOnboarding";
import BrandDashboard from "./pages/brand/BrandDashboard";
import BrandProfile from "./pages/brand/BrandProfile";
import Directory from "./pages/brand/Directory";
import AthleteDetail from "./pages/brand/AthleteDetail";
import Shortlist from "./pages/brand/Shortlist";
import Matcher from "./pages/brand/Matcher";
import { CampaignsList, CampaignDetail } from "./pages/brand/Campaigns";
import Analytics from "./pages/brand/Analytics";
import Organisation from "./pages/brand/Organisation";
import AcceptInvite from "./pages/AcceptInvite";
import VerifyEmail from "./pages/VerifyEmail";
import AdminModeration from "./pages/admin/Moderation";
import AgencyOnboarding from "./pages/agency/AgencyOnboarding";
import Roster from "./pages/agency/Roster";
import Claim from "./pages/Claim";
import Inbox from "./pages/Inbox";
import Conversation from "./pages/Conversation";
import { Toaster } from "./components/ui/sonner";
import "@/App.css";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/for-brands" element={<ForBrands />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/commercial-terms" element={<CommercialTerms />} />
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/kit/:slug" element={<PublicKit />} />
          <Route path="/claim/:token" element={<Claim />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />

          {/* Onboarding (persona-specific) */}
          <Route path="/onboarding" element={<ProtectedRoute requireOnboarding={false}><Onboarding /></ProtectedRoute>} />
          <Route path="/onboarding/brand" element={<ProtectedRoute requireOnboarding={false}><BrandOnboarding /></ProtectedRoute>} />
          <Route path="/onboarding/agency" element={<ProtectedRoute requireOnboarding={false}><AgencyOnboarding /></ProtectedRoute>} />

          {/* Shared in-app entry — DashboardRouter picks the right dashboard per persona */}
          <Route path="/app/dashboard" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />

          {/* Athlete + Agency tools (most routes are usable for athlete persona; agents use them via Acting-As) */}
          <Route path="/app/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/app/media-kit" element={<ProtectedRoute><MediaKit /></ProtectedRoute>} />
          <Route path="/app/crm" element={<ProtectedRoute><CRM /></ProtectedRoute>} />
          <Route path="/app/outreach" element={<ProtectedRoute><Outreach /></ProtectedRoute>} />
          <Route path="/app/brand-directory" element={<ProtectedRoute><BrandDirectory /></ProtectedRoute>} />
          <Route path="/app/brands" element={<Navigate to="/app/brand-directory?tab=all" replace />} />
          <Route path="/app/discovery" element={<Navigate to="/app/brand-directory?tab=matches" replace />} />
          <Route path="/app/sponsor-finder" element={<Navigate to="/app/brand-directory?tab=matches" replace />} />
          <Route path="/app/positioning" element={<ProtectedRoute><Positioning /></ProtectedRoute>} />
          <Route path="/app/deal-assistant" element={<ProtectedRoute><DealAssistant /></ProtectedRoute>} />
          <Route path="/sponsor-finder" element={<Navigate to="/app/sponsor-finder" replace />} />
          <Route path="/app/athletes" element={<ProtectedRoute><Roster /></ProtectedRoute>} />

          {/* Brand persona */}
          <Route path="/app/brand" element={<ProtectedRoute><BrandDashboard /></ProtectedRoute>} />
          <Route path="/app/brand/profile" element={<ProtectedRoute><BrandProfile /></ProtectedRoute>} />
          <Route path="/app/brand/directory" element={<ProtectedRoute><Directory /></ProtectedRoute>} />
          <Route path="/app/brand/shortlist" element={<ProtectedRoute><Shortlist /></ProtectedRoute>} />
          <Route path="/app/brand/athlete/:id" element={<ProtectedRoute><AthleteDetail /></ProtectedRoute>} />
          <Route path="/app/brand/matcher" element={<ProtectedRoute><Matcher /></ProtectedRoute>} />
          <Route path="/app/brand/campaigns" element={<ProtectedRoute><CampaignsList /></ProtectedRoute>} />
          <Route path="/app/brand/campaigns/:id" element={<ProtectedRoute><CampaignDetail /></ProtectedRoute>} />
          <Route path="/app/brand/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
          <Route path="/app/brand/organisation" element={<ProtectedRoute><Organisation /></ProtectedRoute>} />

          {/* Inbox / Conversations — shared across personas */}
          <Route path="/app/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
          <Route path="/app/inbox/:id" element={<ProtectedRoute><Conversation /></ProtectedRoute>} />

          {/* Settings + admin */}
          <Route path="/app/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
          <Route path="/app/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/app/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="/app/admin/moderation" element={<ProtectedRoute><AdminModeration /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
